<template>
  <div
    v-if="isOpen"
    id="food-modal"
    class="modal"
    :style="{ display: 'block' }"
    @click.self="closeModal"
  >
    <div class="modal-content-container">
      <div class="modal-content">
        <div class="food-detail-container" v-if="foodItem">
          <img
            :src="
              foodItem.image_url
                ? $customImageUtil.getImageUrl(foodItem.image_url)
                : DEFAULT_PRODUCT_IMAGE
            "
            :alt="foodItem.alt"
            class="feature-img"
          />

          <!-- Title and Counter -->
          <div class="food-modal-title-container">
            <h3 class="food-modal-title">
              {{ foodItem ? foodItem.name : "" }}
            </h3>
            <div class="food-counter-container">
              <div class="food-counter remove-cart-item">
                <div class="decrease" @click="decreaseQuantity">
                  <IconMinus
                    :width="24"
                    :height="24"
                    stroke="currentColor"
                    :stroke-width="1.5"
                  />
                </div>
                <input
                  type="number"
                  v-model.number="quantity"
                  class="count"
                  min="0"
                />
                <div class="increase" @click="increaseQuantity">
                  <IconPlus
                    :width="24"
                    :height="24"
                    stroke="currentColor"
                    :stroke-width="1.5"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Price & Text -->
          <div class="food-modal-price">
            <IconCurrencyBaht
              :width="10"
              :height="16"
              stroke="#007BDF"
              :stroke-width="1.8"
            />
            <span
              class="price-text-animation"
              :class="{ 'price-updated': animatePrice }"
              >{{ totalPrice.toFixed(2) }}</span
            >
          </div>
          <div class="food-modal-text-content">
            <p>{{ foodItem ? foodItem.description : "" }}</p>
          </div>

          <!-- Addon Options -->
          <div class="size-sweet-container" v-if="filteredAddons.length > 0">
            <div v-for="addon in filteredAddons" :key="addon.id">
              <h4 class="option-title">{{ addon.name }}</h4>
              <div class="size-options radio-input">
                <label
                  class="size-checkbox"
                  v-for="option in addon.options"
                  :key="option.option"
                >
                  <input
                    type="radio"
                    :name="addon.name"
                    :value="option"
                    v-model="selectedOptions[addon.name]"
                  />
                  <div class="with-value">
                    <div class="extra-price">
                      <IconBahtSmall></IconBahtSmall>{{ option.value }}
                    </div>
                    <span>{{ option.option }}</span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- Extra Options -->
          <div v-if="addonExtras.length > 0">
            <template v-for="extra in addonExtras" :key="extra.name">
              <h4 class="option-title addon-extra">{{ extra.name }}</h4>
              <form>
                <div
                  class="extra-options-container no-icons modifier-container"
                >
                  <div class="extra-options">
                    <label
                      v-for="option in extra.options"
                      :key="option.option"
                      class="extra-checkbox"
                    >
                      <input
                        type="checkbox"
                        name="extra"
                        :value="option"
                        v-model="selectedExtras"
                      />
                      <div class="with-value">
                        <!-- <img
                          :src="
                            option.image_url
                              ? $customImageUtil.getImageUrl(option.image_url)
                              : DEFAULT_CATEGORY_IMAGE
                          "
                        /> -->
                        <div class="extra-price">
                          <IconBahtSmall></IconBahtSmall>{{ option.value }}
                        </div>
                        <span>{{ option.option }}</span>
                      </div>
                    </label>
                  </div>
                </div>
              </form>
            </template>
          </div>

          <!-- Action Buttons -->
          <div class="modal-actions-container">
            <button
              type="button"
              href="#"
              class="btn btn-charcoal"
              aria-label="Don't add to cart"
              @click="closeModal"
            >
              {{ t("close") }}
            </button>
            <button
              href="#"
              class="btn btn-blue"
              :disabled="!canCreateOrder"
              :aria-label="t('addToCart')"
              @click="addToOrder"
            >
              {{ t("addToCart") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import eventBus from "@/eventBus";
import { v4 as uuidv4 } from "uuid";
import { useOrderStore } from "@/stores/order";
import { useBroadcastComposable } from "@/composables/useBroadcastComposable";
import { useProduct } from "@/composables/useProduct";
import { useOrder } from "@/composables/useOrder";
import IconCurrencyBaht from "@/components/icons/iconCurrencyBaht.vue";
import IconMinus from "@/components/icons/iconMinus.vue";
import IconPlus from "@/components/icons/iconPlus.vue";
import IconBahtSmall from "@/components/icons/iconBahtSmall.vue";
import {
  //   DEFAULT_CATEGORY_IMAGE,
  DEFAULT_PRODUCT_IMAGE,
} from "@/constants/image";

const props = defineProps({
  isOpen: { type: Boolean, required: true },
  foodItem: { type: Object, default: null },
});

const emit = defineEmits(["update:isOpen"]);

const { t } = useI18n();

const PRICE_ANIMATION_DURATION = 500;

const { broadcastOrderToCds } = useBroadcastComposable();
const { getOrderItems, updateOrderItems } = useOrderStore();
const { calculateProductPrice } = useProduct();
const { canCreateOrder, isSameOrderItem } = useOrder();

const totalPrice = ref(0);
const quantity = ref(1);
const selectedExtras = ref([]);
const selectedOptions = ref({});
const animatePrice = ref(false);

const filteredAddons = computed(() => {
  if (!props.foodItem || !props.foodItem.addons) return [];
  return props.foodItem.addons.filter((addon) => addon.is_required);
});

const addonExtras = computed(() => {
  if (!props.foodItem || !props.foodItem.addons) return [];
  return props.foodItem.addons.filter((addon) => !addon.is_required);
});

const setDefaultOptions = () => {
  if (props.foodItem && props.foodItem.addons) {
    const addOns = props.foodItem.addons.filter((addon) => addon.is_required);
    addOns.forEach((addon) => {
      if (!selectedOptions.value[addon.name]) {
        selectedOptions.value[addon.name] = addon.options?.[0] || null;
      }
    });
  }
};

const updateTotalPrice = () => {
  totalPrice.value =
    calculateProductPrice(
      props.foodItem,
      selectedOptions.value,
      selectedExtras.value
    ) * quantity.value;
};

const resetOptions = () => {
  quantity.value = 1;
  selectedExtras.value = [];
  selectedOptions.value = {};
  setDefaultOptions();
};

const closeModal = () => {
  emit("update:isOpen", false);
};

const increaseQuantity = () => {
  quantity.value++;
};
const decreaseQuantity = () => {
  if (quantity.value > 0) quantity.value--;
};

const addToOrder = () => {
  addNewItemToOrder({
    id: uuidv4(),
    food: props.foodItem,
    quantity: quantity.value,
    options: selectedOptions.value,
    extras: selectedExtras.value,
    price: totalPrice.value,
  });
  closeModal();
  // Emit event to trigger initializeWithRetry in currentOrderPanel.vue
  eventBus.emit("orderModified");
};

const addNewItemToOrder = (orderItem) => {
  const orderItems = getOrderItems();
  const existingItem = orderItems.find((item) =>
    isSameOrderItem(item, orderItem)
  );
  if (existingItem) {
    existingItem.quantity += orderItem.quantity;
    existingItem.price = existingItem.price + orderItem.price;
  } else {
    orderItems.push(orderItem);
  }
  updateOrderItems(orderItems);
  broadcastOrderToCds(10);
};

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      resetOptions();
    }
  }
);

watch(
  () => quantity.value,
  () => {
    if (quantity.value === 0) closeModal();
  }
);

watch(
  () => props.foodItem,
  () => {
    setDefaultOptions();
  },
  { immediate: true }
);

watch(totalPrice, (newPrice, oldPrice) => {
  if (animatePrice.value == true) return;
  if (newPrice !== oldPrice) {
    animatePrice.value = true;
    setTimeout(() => {
      animatePrice.value = false;
    }, PRICE_ANIMATION_DURATION);
  }
});

watch(selectedOptions, updateTotalPrice, { deep: true });
watch(selectedExtras, updateTotalPrice);
watch(quantity, updateTotalPrice);
</script>
